import React from "react";
import Modal from "ui/Modal";
import { Drawer, DrawerContent } from "ui/Drawer";
import { CircleCheck, CircleX } from "lucide-react";
import { Impact } from "util/constants";
import { useUser } from "util/UserContext";
import AlignmentForm from "./AlignmentForm";
import { toast } from "sonner";

interface AlignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  teabate: Impact;
  stance: "agree" | "disagree";
  editMode?: boolean;
  onOpenArgumentModal?: (teabate: Impact, stance: "agree" | "disagree") => void;
  onSaved?: (payload: {
    teabateId: number;
    response: number;
    importance: number;
    note: string;
    privacy: import("util/userUtils").PrivacyLevel;
  }) => void | Promise<void>;
}

const AlignmentModal: React.FC<AlignmentModalProps> = ({
  isOpen,
  onClose,
  teabate,
  stance,
  editMode = false,
  onOpenArgumentModal,
  onSaved,
}) => {
  const { userIsMobile } = useUser();

  const handleSubmitSuccess = () => {
    onClose();
    
    if (!editMode) {
      // Show toast with argument option only for new alignments
      const stanceText = stance === "agree" ? "Support" : "Opposition";
      
      toast.success(`${stanceText} added. Back it up with an argument?`, {
        duration: 3200,
        action: onOpenArgumentModal ? {
          label: "Add argument",
          onClick: () => onOpenArgumentModal(teabate, stance),
        } : undefined,
      });
    } else {
      toast.success('Your perspective has been updated!');
    }
  };

  const modalTitle = (
    <div className="AlignmentModal flex items-center">
      {stance === "agree" 
        ? <CircleCheck className="inline-block mr-2 text-green-600" /> 
        : <CircleX className="inline-block mr-2 text-red-600" />}
      {editMode ? "Update your perspective" : "Share your perspective on your profile"}
    </div>
  );

  const content = (
    <div className="p-3 w-full">
      <AlignmentForm
        teabate={teabate}
        onSubmitSuccess={handleSubmitSuccess}
        onSaved={onSaved}
        showCancelButton={true}
        onCancel={onClose}
        submitButtonText={editMode ? "Update" : "Save"}
        initialResponse={editMode ? undefined : stance === "agree" ? 1 : -1}
        editMode={editMode}
        className=""
      />
    </div>
  );

  if (userIsMobile) {
    return (
      <Drawer
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            onClose();
          }
        }}
      >
        <DrawerContent header={modalTitle}>
          {content}
        </DrawerContent>
      </Drawer>
    );
  } else {

 return (
    <Modal isOpen={isOpen} onClose={onClose} title={modalTitle}>
      {content}
    </Modal>
  );
  }
};

export default AlignmentModal;